"use client";

import { useEffect, useRef } from "react";

/**
 * Smoothly scrolls to the package-cards section whenever the active filter
 * (category / theme) changes, so filtering from the sidebar widgets doesn't
 * leave the user stranded at their previous scroll position.
 *
 * Why a component driven by props (not the `#package-list-wrapper` hash on the
 * filter links): on the 2nd+ filter click the hash is unchanged, so the browser
 * never re-scrolls to it. Passing the current filter as props means this runs on
 * the server-component re-render that each filter navigation triggers, while the
 * component instance persists across the soft navigation — so the `isFirst`
 * guard can skip the initial page load. Renders nothing.
 */
export default function ScrollToPackagesOnFilter({
  category,
  theme,
}: {
  category: string;
  theme: string;
}) {
  const isFirst = useRef(true);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    const target = document.getElementById("package-list-wrapper");
    if (!target) return;
    // Offset by the sticky header height so the section isn't tucked beneath it.
    const header = document.querySelector<HTMLElement>(".sticky-wrapper");
    const offset = (header?.offsetHeight ?? 0) + 16;
    const y = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  }, [category, theme]);

  return null;
}
